// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState } from 'react';

import type { ResourceView } from '../../../api/types';
import { RowThumb } from './RowThumb';
import styles from './Tabel.module.css';

/**
 * A `RowThumb` that only fetches once its row scrolls near the view: "Fold
 * alle ud" on a large survey opens thousands of sub-rows, and each thumbnail
 * is an image request. Until then (and with no view) it is RowThumb's quiet
 * empty box.
 */
export function LazyRowThumb({ view }: { view: ResourceView | null }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || view === null) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, view]);
  if (near || view === null) return <RowThumb view={view} />;
  return <span ref={ref} className={`${styles.thumb} ${styles.thumbNone}`} aria-hidden="true" />;
}
